import {
	ReactElement,
	useMemo,
} from "react";
import { HoursColumn } from "./HoursColumn/HoursColumn";
import { CalendarLayout } from "../CalendarLayout/CalendarLayout";
import { DayColumn } from "./DayColumn/DayColumn";
import { Guid, newGuid } from "client_legacy/ndaw/guid";
import { TimeZone } from "client/DateAndTime/timezones";
import { getIsToday } from "client/DateAndTime/dateTimeUtils";
import { DateOnly } from "client/DateAndTime/dateOnly";
import { convertToDateTime } from "client/DateAndTime/dateUtils";
import { CalendarAppointment, EditAppointmentInfo } from "../../CalendarPage";
import {
	DayWithAssociatedAppointments,
	listAppointmentsAssociatedWithDay
} from "./getEventsForDay";
import { Coordinate } from "client/Misc/commonTypes";
import { TimeOnly } from "client/DateAndTime/timeOnly";
import { AppointmentOrIdentifier } from "../../common";

export type EventsProps = {
	appointments: readonly CalendarAppointment[];
	dateRange: readonly DateOnly[];
	timezone: TimeZone;
	today: Date;
	onOpenDetailsDialog(appointmentOrIdentifier: AppointmentOrIdentifier): void;
	onCreate: (
		coordinate: Coordinate,
		date: DateOnly,
		time?: TimeOnly
	) => void;
	onEdit: (
		coordinate: Coordinate,
		data: EditAppointmentInfo
	) => void;
	onAfterMutatingAppointment: (calendarIds: readonly string[]) => void;
	editingAppointmentId: string | undefined;
};

export const Events = (props: EventsProps): ReactElement => {
	const {
		appointments,
		dateRange,
		timezone,
		today,
		onOpenDetailsDialog,
		onCreate,
		onEdit,
		onAfterMutatingAppointment,
		editingAppointmentId
	} = props;

	const daysWithEvents = useMemo(
		(): readonly Day[] => {
			const daysWithAssociatedAppointments: readonly DayWithAssociatedAppointments[] = listAppointmentsAssociatedWithDay(
				appointments,
				dateRange,
				timezone
			);

			return daysWithAssociatedAppointments.map(
				(x) => {
					const dateTime: Date = convertToDateTime(x.day, timezone);

					return ({
						uniqueId: newGuid(),
						isToday: getIsToday(dateTime, timezone, today),
						date: dateTime,
						appointments: x.appointments
					});
				}
			);
		},
		[
			appointments,
			dateRange,
			timezone,
			today
		]
	);

	return (
		<CalendarLayout trackingId="calendar-events">
			<HoursColumn timezone={timezone} />
			{daysWithEvents.map(d => (
				<DayColumn
					key={d.uniqueId}
					appointments={d.appointments}
					day={d.date}
					isToday={d.isToday}
					timezone={timezone}
					onOpenDetailsDialog={onOpenDetailsDialog}
					onCreate={onCreate}
					onEdit={onEdit}
					onAfterMutatingAppointment={onAfterMutatingAppointment}
					editingAppointmentId={editingAppointmentId}
				/>
			))}
		</CalendarLayout>
	);
};

Events.displayName = "Events";

type Day = {
	uniqueId: Guid;
	isToday: boolean;
	date: Date;
	appointments: readonly CalendarAppointment[];
};